import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
});

// CSRF Double-Submit: lê o cookie kn_csrf_token (não-httpOnly) e envia no header
export function getCsrfToken() {
  const match = document.cookie.match(/(?:^|;\s*)kn_csrf_token=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const csrf = getCsrfToken();
    if (csrf) {
      config.headers['X-CSRF-Token'] = csrf;
    }
  }
  return config;
});

// Handle auth errors — em 401, tenta 1x renovar a sessão via refresh token e repete
let refreshPromise = null;

function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_URL}/api/auth/refresh`, null, { withCredentials: true })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || '';
    const detail = error.response?.data?.detail || '';
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/logout');
    // 403 por token CSRF ausente/expirado: o cookie CSRF (não-httpOnly) pode ter
    // sido descartado pelo navegador. Renovar a sessão re-emite o cookie CSRF.
    const isCsrfError = status === 403 && typeof detail === 'string' && detail.toLowerCase().includes('csrf');

    if ((status === 401 || isCsrfError) && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      try {
        await refreshSession();
        const csrf = getCsrfToken();
        if (csrf) {
          original.headers = { ...original.headers, 'X-CSRF-Token': csrf };
        }
        return api(original);
      } catch (e) {
        // refresh falhou → sessão realmente expirada
      }
    }

    if (status === 401 && !window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
    // CSRF-403 que não pôde ser recuperado (refresh falhou) → sessão expirada
    if (isCsrfError && original._retry && !window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');
export const getExpiringLicenses = (category) => {
  const params = category ? { category } : {};
  return api.get('/dashboard/expiring', { params });
};
export const getCriticalLicenses = () => api.get('/licenses', { params: { is_critical: true } });

// Licenses
export const getLicenses = (params) => api.get('/licenses', { params });
export const getLicense = (id) => api.get(`/licenses/${id}`);
export const createLicense = (data) => api.post('/licenses', data);
export const updateLicense = (id, data) => api.put(`/licenses/${id}`, data);
export const deleteLicense = (id) => api.delete(`/licenses/${id}`);
export const uploadLicenseFile = (id, file, opts = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  if (opts.kind) formData.append('kind', opts.kind);
  if (opts.issue_date) formData.append('issue_date', opts.issue_date);
  if (opts.expiry_date) formData.append('expiry_date', opts.expiry_date);
  if (opts.indeterminate_expiry !== undefined && opts.indeterminate_expiry !== null) {
    formData.append('indeterminate_expiry', String(opts.indeterminate_expiry));
  }
  return api.post(`/licenses/${id}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const validateAttachmentWithAI = (licenseId, attachmentId) =>
  api.post(`/licenses/${licenseId}/attachments/${attachmentId}/validate-ai`, null, { timeout: 120000 });
export const deleteLicenseAttachment = (licenseId, attachmentId) => api.delete(`/licenses/${licenseId}/attachments/${attachmentId}`);

// AI review / correction (Gemini)
export const aiReviewLicense = (licenseId) => api.post(`/licenses/${licenseId}/ai-review`, null, { timeout: 120000 });
export const aiApplyCorrections = (licenseId, changes) => api.post(`/licenses/${licenseId}/ai-review/apply`, { changes });
export const aiReviewBulkStart = (category) => api.post('/licenses/ai-review/bulk', category ? { category } : {});
export const aiReviewBulkStatus = (runId) => api.get(`/licenses/ai-review/bulk/${runId}`);
export const getAiConfig = () => api.get('/settings/ai');
export const updateAiConfig = (data) => api.put('/settings/ai', data);
export const testAiConnection = () => api.post('/settings/ai/test', null, { timeout: 60000 });

// Export/Import
export const getExportUrl = (format, params) => {
  const queryString = new URLSearchParams(params).toString();
  return `${API_URL}/api/licenses/export/${format}?${queryString}`;
};

export const downloadExport = async (format, params = {}) => {
  const response = await api.get(`/licenses/export/${format}`, {
    params,
    responseType: 'blob'
  });
  return response;
};

export const importLicenses = (file, files = []) => {
  const formData = new FormData();
  formData.append('file', file);
  (files || []).forEach((f) => formData.append('files', f));
  return api.post('/licenses/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getImportTemplate = () => `${API_URL}/api/licenses/import/template`;

// Users
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/auth/register', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const adminResetPassword = (id, newPassword) => api.put(`/users/${id}/password`, { new_password: newPassword });
export const adminResetMfa = (id) => api.post(`/users/${id}/mfa/reset`);
export const importUsers = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/users/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const downloadUsersTemplate = async () => {
  const response = await api.get('/users/import/template', { responseType: 'blob' });
  return response;
};
export const getUserDuplicates = () => api.get('/users/duplicates');
export const removeUserDuplicates = () => api.post('/users/duplicates/remove');

// MFA / 2FA TOTP
export const mfaSetup = () => api.post('/auth/mfa/setup');
export const mfaEnable = (code) => api.post('/auth/mfa/enable', { code });
export const mfaDisable = (password, code) => api.post('/auth/mfa/disable', { password, code });

// Sessões ativas
export const getSessions = () => api.get('/auth/sessions');
export const revokeSession = (familyId) => api.delete(`/auth/sessions/${familyId}`);
export const revokeOtherSessions = () => api.delete('/auth/sessions/others');

// Profile (self)
export const updateMyProfile = (data) => api.put('/auth/profile', data);
export const changeMyPassword = (currentPassword, newPassword) => api.put('/auth/password', { current_password: currentPassword, new_password: newPassword });

// Audit Logs
export const getAuditLogs = (params) => api.get('/audit-logs', { params });
export const exportAuditLogs = (params = {}) => api.get('/audit-logs/export/excel', { params, responseType: 'blob' });
export const getSecurityEvents = (params = {}) => api.get('/security/events', { params });
export const getLicenseHistory = (licenseId) => api.get(`/audit-logs/license/${licenseId}`);

// Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);
export const getSmtpConfig = () => api.get('/settings/smtp');
export const updateSmtpConfig = (data) => api.put('/settings/smtp', data);
export const getStorageConfig = () => api.get('/settings/storage');
export const updateStorageConfig = (data) => api.put('/settings/storage', data);
export const testStorageConnection = () => api.post('/settings/storage/test');
export const migrateStorageToS3 = () => api.post('/settings/storage/migrate', {}, { timeout: 0 });

// Notifications
export const sendTestEmail = () => api.post('/notifications/test');
export const sendNotifications = () => api.post('/notifications/send');
export const getNotificationLogs = () => api.get('/notifications/logs');
export const runExpiryCheck = () => api.post('/notifications/run-expiry-check');
export const getNotificationDispatches = () => api.get('/notifications/dispatches');

// User Permissions
export const getUserPermissions = (userId) => api.get(`/users/${userId}/permissions`);
export const updateUserPermissions = (userId, permissions) => api.put(`/users/${userId}/permissions`, permissions);

// Tickets
export const getTickets = (params) => api.get('/tickets', { params });
export const getTicketStats = () => api.get('/tickets/stats');
export const getTicketCategories = () => api.get('/tickets/categories');
export const getTicketAssignees = () => api.get('/tickets/assignees');
export const getTicket = (id) => api.get(`/tickets/${id}`);
export const createTicket = (data) => api.post('/tickets', data);
export const updateTicket = (id, data) => api.put(`/tickets/${id}`, data);
export const updateTicketStatus = (id, status) => api.put(`/tickets/${id}/status?status=${status}`);
export const addTicketMessage = (id, data) => api.post(`/tickets/${id}/messages`, data);
export const uploadTicketAttachment = (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/tickets/${id}/messages/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const exportTickets = async (format, params = {}) => {
  const response = await api.get(`/tickets/export/${format}`, {
    params,
    responseType: 'blob'
  });
  return response;
};

// In-app Notifications (Inbox)
export const getInbox = (params) => api.get('/notifications/inbox', { params });
export const getInboxCount = () => api.get('/notifications/inbox/count');
export const markNotificationRead = (id) => api.put(`/notifications/inbox/${id}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/inbox/read-all');
export const deleteNotification = (id) => api.delete(`/notifications/inbox/${id}`);

// Users lookup (lightweight list for selectors)
export const getUsersLookup = () => api.get('/users/lookup');

// Backups
export const getBackupSettings = () => api.get('/backups/settings');
export const updateBackupSettings = (data) => api.put('/backups/settings', data);
export const triggerBackup = () => api.post('/backups');
export const listBackups = () => api.get('/backups');
export const downloadBackup = async (id) => {
  const response = await api.get(`/backups/${id}/download`, { responseType: 'blob' });
  return response;
};
export const deleteBackup = (id) => api.delete(`/backups/${id}`);
export const restoreBackup = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/backups/restore', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export default api;
